import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FormGroup, Label, Input, Button, Alert } from "reactstrap";

// import ControldocFormTemplateFilePicker from './file_picker';

import MediaImagePicker from '../../media/image/image_picker';
import { create as createImage } from '../../media/image/axios';

import FieldError from "../../helper/FieldError";

import {
  inputId,
  inputName,
  drawErrors,
  drawTooltipHelp
} from "../../helper/form";
import {
  montserratFontCssUrl,
  templateTinymceToolbar,
  tinymceFontFamilyFormats,
} from "../helper";

import Select from 'react-select'

const ControldocFormTemplateForm = props => {
  const formName = props?.formName || "controdoc_form_template";
  const currentUser = props?.currentUser || {};
  const { indexPath, showPath, createAction, updateAction} = props?.actions || {};
  const currentEntityType = props?.currentEntityType || {};
  const currentCustomer = props?.currentCustomer || {};

  const [template, setTemplate] = useState(props?.template || {});
  const [templateName, setTemplateName] = useState(props?.template?.name);
  const [isControldocConsentAnnex, setIsControldocConsentAnnex] = useState(props?.template?.is_controldoc_consent_annex);
  const [templateLayoutId, setTemplateLayoutId] = useState(props?.template?.template_layout_id);
  const [templateContent, setTemplateContent] = useState(props?.template?.content)
  const [templateVars, setTemplateVars] = useState([]);
  const [templateDocumentTypeId, setTemplateDocumentTypeId] = useState(props?.template?.document_type_id);

  const [requireUploadedTemplate, setRequireUploadedTemplate] = useState(props?.template?.require_uploaded_template);
  const [uploadedFile, setUploadedFile] = useState(null);

  const [tinymceEditor, setTinymceEditor] = useState();
  const [openFilePicker, setOpenFilePicker] = useState(false);
  const [imageList, setImageList] = useState(props?.imagePicker?.imageList || []);
  const allowedFormatImagePicker = props?.imagePicker?.allowedFormatFiles || []

  const [errors, setErrors] = useState(props?.errors || {})

  const uploadedFileAllowedTypes = props?.data?.uploadedFileAllowedTypes;

  const insertImageInEditor = useCallback((editor, imageData) => {
    editor.insertContent(`
      <img
        src="${imageData.preview_url}"
        alt="${imageData.filename}"
        width="300"
        height="auto"
      />`);
  }, []);

  const uploadMediaImageFile = useCallback((file) => {
    const formData = new FormData();
    formData.append("media_image[media]", file, file.name || "image");

    return new Promise((resolve, reject) => {
      createImage(formData, response => {
        if (response?.status === 201) {
          setImageList(prev => [...prev, response.data]);
          resolve(response.data);
        } else {
          const message = response?.data?.media?.[0] || "No se pudo subir la imagen";
          reject(message);
        }
      });
    });
  }, []);

  const varsDataTypes = [
    {
      name: "date",
      label: "Fecha",
      type: "date",
      input_type: "date",
      class: "",
    },
    {
      name: "text",
      label: "Texto",
      type: "text",
      input_type: "text",
      class: "",
    },
    {
      name: "money",
      label: "Formato Moneda",
      type: "money",
      input_type: "text",
      class: "input-money",
    },
    {
      name: "number",
      label: "Número",
      type: "number",
      input_type: "number",
      class: "",
    },
  ];

  useEffect(() => {
    const contentId = inputId(formName, "content");
    const contentSelector = `#${contentId}`;
    const tinymceGlobal = window.tinyMCE || window.tinymce;
    let timeoutId = null;

    const initEditor = () => {
      if (!document.querySelector(contentSelector)) return;
      if (typeof tinymceGlobal === "undefined") {
        timeoutId = window.setTimeout(initEditor, 50);
        return;
      }

      tinymceGlobal.init({
        selector: contentSelector,
        language: "es",
        height: 500,
        entity_encoding: "raw",
        plugins: "preview advlist lists table charmap fullscreen link image pagebreak code",
        toolbar: templateTinymceToolbar,
        image_caption: true,
        a11y_advanced_options: true,
        relative_urls: false,
        remove_script_host: false,
        pagebreak_separator: "<div class='alwaysbreak'></div>",
        promotion: false,
        content_css: [montserratFontCssUrl],
        font_family_formats: tinymceFontFamilyFormats,
        automatic_uploads: true,
        paste_data_images: true,
        images_file_types: "jpeg,jpg,png",
        images_upload_handler: (blobInfo, progress) => {
          return uploadMediaImageFile(blobInfo.blob())
            .then(imageData => imageData.preview_url)
            .catch(message => Promise.reject({ message, remove: true }));
        },

        setup: editor => {
          editor.on("drop", e => {
            const files = e.dataTransfer?.files;
            if (!files?.length) return;

            const imageFile = Array.from(files).find(file => file.type.startsWith("image/"));
            if (!imageFile) return;

            e.preventDefault();
            e.stopPropagation();

            uploadMediaImageFile(imageFile)
              .then(imageData => insertImageInEditor(editor, imageData))
              .catch(message => {
                editor.notificationManager.open({
                  text: message,
                  type: "error",
                });
              });
          });

          editor.ui.registry.addButton('uploadFile', {
            text: 'Imágenes',
            icon: 'image',
            onAction: _ => {
              setTinymceEditor(editor)
              setOpenFilePicker(true)
            }
          });


          editor.ui.registry.addMenuButton("varMenuButton", {
            text: "Crear Variables",
            fetch: callback => {
              var items = _.map(varsDataTypes, function (_var) {
                return {
                  type: "menuitem",
                  text: _var.label,
                  onAction: () => {
                    let content = editor.selection.getContent();
                    const nonAlphanumericRegex = /([^a-zA-Z\d:])/g;
                    const whitespaceRegex = /([\s])/g;

                    content =
                      _.deburr(content)
                        .replaceAll(nonAlphanumericRegex, "_")
                        .replaceAll(whitespaceRegex, "_")
                        .replaceAll(".", "")
                        .toLowerCase() || _.uniqueId(`${_var.name}_`);
                    editor.insertContent(`[[${content}]/${_var.name}/]`);
                  },
                };
              });
              callback(items);
            },
          });
        },
        init_instance_callback: editor => {
          editor.setContent(templateContent || "");
          editor.on("Change", e => {
            let text = e.target.getContent();
            let vars = getAllVariableFromText(text);
            setTemplateContent(text);
            setTemplateVars(vars);
          });
        },
      });
    };

    timeoutId = window.setTimeout(initEditor, 0);

    return () => {
      if (timeoutId != null) window.clearTimeout(timeoutId);
      if (tinymceGlobal) {
        try {
          tinymceGlobal.remove();
        } catch (error) {
          console.error(error);
        }
      }
    };
  }, [formName, requireUploadedTemplate, uploadMediaImageFile, insertImageInEditor]);

  useEffect(() => {
    let _vars = getAllVariableFromText(templateContent || "");
    setTemplateVars(_vars);
  }, []);

  const onUploadedFileChange = (e) => {
    const file = e?.target?.files?.[0];
    setUploadedFile(file);
  };

  const getAllVariableFromText = data => {
    // let re = /(\[\[\w+\]\])+/g;
    let re = /(\[\[\w+\]\/\w+\/\])+/g;

    let _vars = data.match(re);

    let vars = _.map(_vars, function (_var) {
      var __reType = /\/\w+\/+/g;
      var __type = _var.match(__reType)[0];

      // Para quitar los corchetes y obtener solo el valor:
      // Por ejemplo, para '[[empresa]]' => 'empresa'
      let key = _var.replace(__type, "").replace(/^\[\[|\]\]$/g, "");

      let info =  {
        key: key,
        var: _var,
        name: _var.replace(__type, ""),
        type: __type?.replaceAll("/", ""),
      };


      console.log(info)

      return info
    });

    return vars;
  };

  const drawVariables = () => {
    if (requireUploadedTemplate) return;

    if(templateVars.length > 0){
      return(
        <div className="card">
          <div className="card-body">
            <h6 className="card-subtitle mb-2 text-muted">
              Variables utilizadas
            </h6>

            { _.map(templateVars, function(_var, index){
              return(
                <span
                  className="badge badge-secondary mr-2"
                  key={ _.uniqueId("span-var") }
                >
                  { `${ _var.name } - ${ _var.type}` }
                </span>
              )
            })}

          </div>
        </div>
      )
    } else {
      return(
        <div className="card">
          <div className="card-body">
            <Alert color="warning" className="text-center">
              Aún no tienes variables
            </Alert>

            { drawErrorsVars() }
          </div>
        </div>
      )
    }
  }

  const drawErrorsVars = () => {
    if(_.has(errors, "vars")){
      return(
        <ul className="errors_list">
          { _.map(errors["vars"], function(error){
            return(
              <li key={ _.uniqueId("error_vars") }>{ error }</li>
            )
          })}
        </ul>
      )
    }
  }

  const getTemplateLayoutSelectedOption = () => {
    let selected = _.find(props?.data?.templateLayouts, { id: templateLayoutId })

    if(selected){
      return { value: selected.id, label: selected.name }
    } else {
      return null;
    }
  }

  const drawSelectTemplateLayoutOptions = () => {
    return _.map(props?.data?.templateLayouts, templateLayout => ( {label: templateLayout.name, value: templateLayout.id }))
  }

  const drawTemplateLayoutsInputSelect = () => {
    return(
      <FormGroup>
        <Label
          for={ inputId(formName, "template_layout_id") }
        >
          { I18n.t("activerecord.attributes.controldoc_form/template.template_layout_id") }
        </Label>

        <Select
          styles={{
            // Fixes the overlapping problem of the component
            menu: provided => ({ ...provided, zIndex: 9999 })
          }}
          id={ inputId(formName, 'template_layout_id') }
          name={ inputName(formName, 'template_layout_id') }
          invalid={ _.has(errors, 'template_layout_id') }
          value={ getTemplateLayoutSelectedOption() }
          onChange={ e => setTemplateLayoutId( e?.value )}
          options={ drawSelectTemplateLayoutOptions() }
          isClearable={ true }
          placeholder={ `-- Selecciona un ${ I18n.t('activerecord.attributes.controldoc_form/template.template_layout_id') } --` }
        />

        { drawErrors(errors, "template_layout_id") }
      </FormGroup>
    )
  }

  const getFormData = () => {
    let formData = new FormData();

    formData.append(`${ formName }[id]`, template?.id || "");
    formData.append(`${ formName }[name]`, templateName || "");
    formData.append(`${ formName }[content]`, templateContent || "");
    formData.append(`${ formName }[parent_id]`, template?.parent_id || "");
    formData.append(`${ formName }[is_controldoc_consent_annex]`, isControldocConsentAnnex);
    formData.append(`${ formName }[template_layout_id]`, templateLayoutId || '');

    if (currentCustomer?.client_type_arauco) {
      formData.append(`${formName}[document_type_id]`, templateDocumentTypeId || '');
      formData.append(`${formName}[require_uploaded_template]`, requireUploadedTemplate ? '1' : '0');

      if (uploadedFile) formData.append(`${formName}[uploaded_file]`, uploadedFile);
    }

    _.each(templateVars, _var => {
      formData.append(
        `${ formName }[vars][${ _var.key }][name]`, _var.name
      )

      formData.append(
        `${ formName }[vars][${ _var.key }][type]`, _var.type
      )

      formData.append(
        `${ formName }[vars][${ _var.key }][var]`, _var.var
      )
    });

    return formData;
  }

  const handleSubmit = e => {
    swalWithBootstrap.fire({
      title: I18n.t("controldoc_form.templates.form.confirm.title"),
      html: I18n.t("controldoc_form.templates.form.confirm.message"),
    }).then( result => {
      if(result.isConfirmed){
        handleSave();
      }
    })
  }

  const handleSave = () => {
    let formData = getFormData();

    // Si existe en currentEntityType, la ruta cambie por el parametro del EntityType para el modulo
    if(_.keys(currentEntityType).length > 1) {
      if(template.id){
        updateAction({id: template.id, currentEntityTypeId: currentEntityType.hashid}, formData, response => {
          handleResponse(response)
        })
      } else {
        createAction(currentEntityType.hashid, formData, response => {
          handleResponse(response)
        })
      }
    } else {
      // Cuando no tiene el entity type
      if(template.id){
        updateAction(template.id, formData, response => {
          handleResponse(response);
        })
      } else{
        createAction(formData, response => {
          handleResponse(response);
        })
      }
    }
  }

  const handleResponse = response => {
    if(response.status == 200 || response.status == 201){

      if(_.keys(currentEntityType).length > 1){
        window.location = showPath(currentEntityType.hashid, response.data.id);
      } else {
        window.location = showPath(response.data.id);
      }
    } else {
      setErrors(response.data);
    }
  }

  const requireUploadedTemplateSwitchInput = () => {
    if (currentCustomer?.client_type_arauco){
      return (
        <FormGroup>
          <div className="custom-switch">
            <FieldError errors={errors?.require_uploaded_template || []}>
              <Input
                className="custom-control-input"
                type="checkbox"
                name={ inputName(formName, 'require_uploaded_template') }
                id={ inputId(formName, 'require_uploaded_template') }
                onChange={ e => setRequireUploadedTemplate(e.target.checked) }
                checked={ requireUploadedTemplate }
                value="1"
              />
              <Label className="custom-control-label" for={ inputId(formName, 'require_uploaded_template') }>
                { I18n.t('activerecord.attributes.controldoc_form/template.require_uploaded_template') }
                { drawTooltipHelp(I18n.t('activerecord.help.controldoc_form/template.require_uploaded_template')) }
              </Label>
            </FieldError>
          </div>
        </FormGroup>
      );
    }
  };

  const uploadedFileInput = () => {
    if (currentCustomer?.client_type_arauco && requireUploadedTemplate){
      return (
        <FormGroup>
          <FieldError errors={_.concat(errors?.uploaded_file || [], errors?.vars || [])}>
            <Label className="required" for={ inputId(formName, 'uploaded_file') }>
              { I18n.t('activerecord.attributes.controldoc_form/template.uploaded_file') }
            </Label>
            <Input
              type="file"
              id={ inputId(formName, 'uploaded_file') }
              name={ inputName(formName, 'uploaded_file') }
              accept={ uploadedFileAllowedTypes }
              onChange={ e => onUploadedFileChange(e) }
              invalid={ _.has(errors, 'uploaded_file') || _.has(errors, 'vars') }
            />
          </FieldError>
        </FormGroup>
      );
    }
  };

  const drawIsControldocConsentAnnexSwitchInput = () => {
    return(
      <FormGroup>
        <div className='custom-switch'>
          <Input
            className='custom-control-input'
            type='checkbox'
            name={ inputName(formName, 'is_controldoc_consent_annex') }
            id={ inputId(formName, 'is_controldoc_consent_annex') }
            onChange={ e => setIsControldocConsentAnnex(e.target.checked) }
            checked={ isControldocConsentAnnex }
            value={ '1' }

          />
          <Label
            className='custom-control-label'
            for={ inputId(formName, 'is_controldoc_consent_annex') }
          >
            { I18n.t(`activerecord.attributes.controldoc_form/template.is_controldoc_consent_annex?`) }
            { drawTooltipHelp(I18n.t('controldoc_form.templates.form.is_controldoc_consent_annex_help')) }
          </Label>
        </div>
      </FormGroup>
    )
  }

  const drawSelectDocumentTypeOptions = useMemo(() => {
    return _.map(props?.data?.documentTypes, documentType => ({ value: documentType.id, label: documentType.name }))
  }, [props?.data?.documentTypes])

  const documentTypeSelectedOption = useMemo(() => {
    return _.find(drawSelectDocumentTypeOptions, { value: templateDocumentTypeId })
  }, [templateDocumentTypeId])

  const drawDocumentTypesInputSelect = () => {
    if (currentCustomer.client_type_arauco) {
      return (
        <FormGroup>
          <FieldError errors={errors?.document_type_id || []}>
            <Label className="required"
              for={inputId(formName, "document_type_id")}
            >
              {I18n.t("activerecord.attributes.controldoc_form/template.document_type_id")}
            </Label>

            <Select
              id={inputId(formName, "document_type_id")}
              name={inputName(formName, "document_type_id")}
              value={documentTypeSelectedOption}
              onChange={e => setTemplateDocumentTypeId(e?.value)}
              options={drawSelectDocumentTypeOptions}
              isClearable={true}
              placeholder={`-- Selecciona un ${I18n.t('activerecord.attributes.controldoc_form/template.document_type_id')} --`}
              styles={{
                // Fixes the overlapping problem of the component
                menu: provided => ({ ...provided, zIndex: 9999 })
              }}
              invalid={_.has(errors, "document_type_id")}
            />
          </FieldError>
        </FormGroup>
      )
    }
  }

  return(
    <div className="row">
      <div className="col-12">
        <MediaImagePicker
          openFilePicker={ openFilePicker }
          setOpenFilePicker={ setOpenFilePicker }
          imageList={ imageList }
          setImageList={ setImageList }
          tinymceEditor={ tinymceEditor }
          allowedFormatImagePicker={ allowedFormatImagePicker }
        />

        <FormGroup>
          <Label
            for={ inputId(formName, "name") }
            className="required"
          >
            { I18n.t("activerecord.attributes.controldoc_form/template.name") }
          </Label>

          <Input
            type="text"
            id={ inputId( formName, "name") }
            name={ inputName(formName, "name") }
            value={ templateName || "" }
            onChange={ e => setTemplateName(e?.target?.value) }
            invalid={ _.has(errors, "name") }
          />

          { drawErrors(errors, "name") }
        </FormGroup>
        { drawIsControldocConsentAnnexSwitchInput() }
        { requireUploadedTemplateSwitchInput() }
        { drawTemplateLayoutsInputSelect() }
        { drawDocumentTypesInputSelect() }
        { uploadedFileInput() }

        <FormGroup>
          { drawVariables() }
        </FormGroup>

        {
          !requireUploadedTemplate && (
            <FormGroup>
              <Label
                for={ inputId(formName, "content") }
                className="required"
              >
                { I18n.t("activerecord.attributes.controldoc_form/template.content") }
              </Label>

              <Input
                type="textarea"
                id={ inputId(formName, "content") }
                name={ inputName(formName, "content") }
                value={ templateContent || "" }
                onChange={ e => setTemplateContent(e?.target?.value) }
              />
              { drawErrors(errors, "content") }
            </FormGroup>
          )
        }

        <FormGroup>
          <div className="col-12 text-end mt-3">
            <a
              href={ indexPath }
              className="btn btn-default"
            >
              { I18n.t("actions.back") }
            </a>

            <Button
              type="button"
              className="ml-2"
              color="success"
              onClick={ e => handleSubmit(e) }
            >
              { I18n.t('actions.save') }
            </Button>
          </div>
        </FormGroup>
      </div>
    </div>
  )
}

export default ControldocFormTemplateForm;
